import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface InitiateCallParams {
  applicationId: string;
  applicantName: string;
  applicantPhone: string;
  jobTitle: string;
  matchScore?: number;
  entityId: string;
}

export interface CallResponse {
  success: boolean;
  callId?: string;
  message?: string;
  error?: string;
  conversation_id?: string;
  callSid?: string;
}

interface SaveCallParams {
  applicationId: string;
  entityId: string;
  conversationId: string;
  callSid: string | null;
  phoneNumber: string;
  applicantName: string;
  jobTitle: string;
}

/**
 * Saves call record to database
 * @param params - Call save parameters
 * @returns Promise<void>
 */
async function saveCallToDatabase(params: SaveCallParams): Promise<void> {
  try {
    const { error } = await supabase
      .from('call_logs')
      .insert({
        application_id: params.applicationId,
        entity_id: params.entityId,
        conversation_id: params.conversationId,
        call_sid: params.callSid,
        phone_number: params.phoneNumber,
        agent_id: 'elevenlabs-agent', // Default agent ID
        status: 'initiated', // Initial status
        metadata: {
          applicant_name: params.applicantName,
          job_title: params.jobTitle,
          initiated_at: new Date().toISOString(),
        },
      });

    if (error) {
      console.error('Error saving call to database:', error);
      throw error;
    }

    console.log('Call record saved successfully:', params.conversationId);
  } catch (error) {
    console.error('Failed to save call record:', error);
    // Don't throw - we don't want to fail the call initiation if DB save fails
  }
}

/**
 * Initiates an AI call to a candidate using ElevenLabs
 * @param params - Call initiation parameters
 * @returns Promise with call response
 */
export async function initiateAICall(params: InitiateCallParams): Promise<CallResponse> {
  try {
    const result = await apiPost(`${API_BASE_URL}/elevenlabs`, { action: 'initiate-call', ...params });

    // Extract conversation_id and callSid from response
    const conversationId = result.conversation_id || null;
    const callSid = result.callSid || null;

    // Save call record to database if we have conversation_id
    if (conversationId) {
      await saveCallToDatabase({
        applicationId: params.applicationId,
        entityId: params.entityId,
        conversationId,
        callSid,
        phoneNumber: params.applicantPhone,
        applicantName: params.applicantName,
        jobTitle: params.jobTitle,
      });
    }

    // Return success response
    return {
      success: true,
      callId: result.callId,
      message: result.message,
      conversation_id: conversationId,
      callSid,
    };
  } catch (error: any) {
    console.error('Error initiating AI call:', error);
    return {
      success: false,
      error: error.message || 'Failed to initiate AI call. Please check your connection and try again.',
    };
  }
}

/**
 * Validates phone number format
 * @param phone - Phone number to validate
 * @returns True if valid, false otherwise
 */
export function validatePhoneNumber(phone: string): boolean {
  // Remove common formatting characters
  const cleanPhone = phone.replace(/[\s\-().]/g, '');

  // Check if it's a valid phone number (10-15 digits, optional + prefix)
  const phoneRegex = /^\+?[1-9]\d{9,14}$/;

  return phoneRegex.test(cleanPhone);
}

/**
 * Formats phone number for display
 * @param phone - Phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  const cleanPhone = phone.replace(/[\s\-().]/g, '');

  // Format US numbers as (XXX) XXX-XXXX
  if (cleanPhone.length === 10) {
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
  }

  // Format US numbers with country code as +1 (XXX) XXX-XXXX
  if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
    return `+1 (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(4, 7)}-${cleanPhone.slice(7)}`;
  }

  // Return as-is for international numbers
  return phone;
}

/**
 * Fetches call status and saves to database
 * @param conversationId - ElevenLabs conversation ID
 * @param applicationId - Application ID
 * @param entityId - Entity ID
 * @returns Promise with call status
 */
export async function fetchAndSaveCallStatus(
  conversationId: string,
  applicationId: string,
  entityId: string
): Promise<CallResponse> {
  try {
    const result = await apiPost(`${API_BASE_URL}/elevenlabs`, {
      action: 'get-status',
      conversationId,
      applicationId,
      entityId
    });

    return {
      success: true,
      message: result.message,
    };
  } catch (error: any) {
    console.error('Error fetching call status:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch call status',
    };
  }
}

/**
 * Updates call status in database directly (for manual updates)
 * @param conversationId - ElevenLabs conversation ID
 * @param status - New status
 * @returns Promise<void>
 */
export async function updateCallStatus(
  conversationId: string,
  status: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('call_logs')
      .update({
        status: status,
        updated_at: new Date().toISOString(),
      })
      .eq('conversation_id', conversationId);

    if (error) {
      console.error('Error updating call status:', error);
      throw error;
    }

    console.log('Call status updated:', conversationId, status);
  } catch (error) {
    console.error('Failed to update call status:', error);
    throw error;
  }
}

/**
 * Triggers Supabase Edge Function to poll call status
 * @param conversationId - Optional specific conversation ID to poll (polls all active if not provided)
 * @returns Promise with polling result
 */
export async function triggerCallStatusPoll(conversationId?: string): Promise<CallResponse> {
  try {
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      return {
        success: false,
        error: 'Supabase configuration missing',
      };
    }

    const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/poll-call-status`;

    const body = conversationId ? { conversation_id: conversationId } : {};

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to trigger poll',
      };
    }

    console.log('Poll triggered:', result);

    return {
      success: true,
      message: result.message,
    };
  } catch (error: any) {
    console.error('Error triggering call status poll:', error);
    return {
      success: false,
      error: error.message || 'Failed to trigger poll',
    };
  }
}

/**
 * Fetches audio URL from ElevenLabs via Supabase Edge Function
 * @param conversationId - ElevenLabs conversation ID
 * @param callId - Database call log ID
 * @returns Promise with audio URL result
 */
export async function fetchCallAudio(
  conversationId: string,
  callId: string
): Promise<CallResponse & { audioUrl?: string }> {
  try {
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      return {
        success: false,
        error: 'Supabase configuration missing',
      };
    }

    const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/fetch-call-audio`;

    console.log('Calling Edge Function to fetch audio:', conversationId);

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        call_id: callId,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Edge Function error:', result);
      return {
        success: false,
        error: result.error || result.message || 'Failed to fetch audio',
      };
    }

    console.log('Audio URL fetched successfully:', result.audio_url);

    return {
      success: true,
      audioUrl: result.audio_url,
      message: result.message || 'Audio fetched successfully',
    };
  } catch (error: any) {
    console.error('Error fetching call audio:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch audio',
    };
  }
}
